import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_NO_ANIMATION,
  chartBaseTooltip,
  chartLegend,
  chartSeriesColor,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

export interface IDonutDataset {
  data: Array<number | null>;
  backgroundColor: string[];
  borderWidth: number;
}

/**
 * Доли круга: цвет по номеру (`CHART_SERIES_PALETTE`) или свой; `null` и
 * отрицательные — доли нет (круг не умеет «меньше нуля»).
 */
export const donutData = (
  labels: ReadonlyArray<string>,
  values: ReadonlyArray<number | null>,
  colors?: ReadonlyArray<string>,
): { labels: string[]; datasets: IDonutDataset[] } => ({
  labels: [...labels],
  datasets: [
    {
      data: labels.map((_, index) => {
        const value = values[index];
        return value === null || value === undefined || value < 0
          ? null
          : value;
      }),
      backgroundColor: labels.map(
        (_, index) => colors?.[index] ?? chartSeriesColor(index),
      ),
      borderWidth: 1,
    },
  ],
});

/** Доля в процентах от суммы видимых долей; сумма 0 — `null`. */
export const donutShare = (
  values: ReadonlyArray<number | null>,
  index: number,
): number | null => {
  const total = values.reduce<number>(
    (sum, value) => sum + (value !== null && value > 0 ? value : 0),
    0,
  );
  const value = values[index];
  if (!total || value === null || value === undefined || value < 0) {
    return null;
  }
  return (value / total) * 100;
};

export interface IDonutChartProps {
  labels: ReadonlyArray<string>;
  values: ReadonlyArray<number | null>;
  /** Свой цвет долей; не задан — общая палитра. */
  colors?: ReadonlyArray<string>;
  /** `pie` — круг без дырки. */
  variant?: 'donut' | 'pie';
  /** Значение в подсказке; не задан — число как есть. */
  format?: (value: number) => string;
  ariaLabel?: string;
}

/**
 * ТЗ 0113 — круг / кольцо (вынесено из `CategoryDonut` финансового
 * дашборда): легенда снизу, в подсказке — значение и доля в процентах.
 */
export const DonutChart = memo(
  ({
    labels,
    values,
    colors,
    variant = 'donut',
    format,
    ariaLabel,
  }: IDonutChartProps) => {
    const data = useMemo(() => donutData(labels, values, colors), [
      labels,
      values,
      colors,
    ]);
    const options: ChartOptions<'doughnut'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        cutout: variant === 'pie' ? 0 : '55%',
        plugins: {
          legend: chartLegend,
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              label: (item) => {
                const raw = Number(item.raw);
                const share = donutShare(values, item.dataIndex);
                const text = format ? format(raw) : String(raw);
                return share === null
                  ? `${item.label}: ${text}`
                  : `${item.label}: ${text} (${Math.round(share)} %)`;
              },
            },
          },
        },
      }),
      [variant, format, values],
    );
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart
          type="doughnut"
          data={data as never}
          options={options as never}
        />
      </C.ChartBox>
    );
  },
);

DonutChart.displayName = 'DonutChart';
